/* ============================================================================
   <product-360-view>
   - Swatch click (panel OR sticky bar) -> fetch variant -> swap 360 stage
     -> re-init plugin -> sync active state across BOTH swatch sets
     -> notify the price module
   - Drag pill visibility (watches the plugin's cursor state)
   - Sticky booking bar: transform-based show/hide between editor thresholds
   ========================================================================== */

if (!customElements.get('product-360-view')) {
  const SWATCH_SELECTOR = '.p360__swatch, .p360-sticky__swatch';

  class Product360View extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.productHandle = this.dataset.handle;
      this.stage = this.querySelector('.p360__stage');
      this.cloudimage = this.querySelector('.cloudimage-360');
      this.cursorObserver = null;
    }

    connectedCallback() {
      this.bindSwatches();
      this.observeCursor();
      this.initSticky();
    }

    disconnectedCallback() {
      if (this.cursorObserver) this.cursorObserver.disconnect();
      this.teardownSticky();
    }

    /* ----------------------- Swatches / variants ------------------------- */

    bindSwatches() {
      // Panel + sticky swatches share the same handler and stay in sync.
      this.querySelectorAll(SWATCH_SELECTOR).forEach((swatch) => {
        swatch.addEventListener('click', this.onSwatchClick.bind(this));
      });
    }

    onSwatchClick(event) {
      const swatch = event.currentTarget;
      const id = swatch.id || swatch.dataset.variantId;
      if (!id || swatch.classList.contains('active')) return;

      this.setActiveSwatch(id);
      this.loadVariant(id);

      const prebook = document.querySelector('.prebook-button');
      if (prebook) prebook.setAttribute('data-variant-id', id);

      this.dispatchEvent(
        new CustomEvent('p360:variantchange', { bubbles: true, detail: { variantId: id } })
      );
    }

    setActiveSwatch(id) {
      let activeColorName = '';
      let activeBadge = '';
      this.querySelectorAll(SWATCH_SELECTOR).forEach((s) => {
        const isActive = (s.id || s.dataset.variantId) === String(id);
        s.classList.toggle('active', isActive);
        s.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        if (isActive) {
          if (!activeColorName) {
            activeColorName = s.getAttribute('aria-label') || s.querySelector('.p360__swatch-name')?.textContent || '';
          }
          const badgeEl = s.querySelector('.p360__swatch-badge');
          if (badgeEl && badgeEl.textContent.trim()) {
            activeBadge = badgeEl.textContent.trim();
          }
        }
      });

      if (activeColorName) {
        const colorLabel = this.querySelector('.p360__panel-selected-color');
        if (colorLabel) colorLabel.textContent = activeColorName.trim();
      }

      const badgeLabel = this.querySelector('.p360__panel-selected-badge');
      if (badgeLabel) {
        if (activeBadge) {
          badgeLabel.textContent = activeBadge;
          badgeLabel.style.display = '';
        } else {
          badgeLabel.textContent = '';
          badgeLabel.style.display = 'none';
        }
      }
    }

    loadVariant(id) {
      if (!this.stage) return;

      // Lock the stage height so the layout doesn't jump while fetching.
      this.stage.style.height = `${this.stage.offsetHeight}px`;

      const url = `/products/${this.productHandle}?variant=${id}&section_id=${this.sectionId}`;

      fetch(url)
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.text();
        })
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const nextStage = doc.querySelector('.p360__stage');

          if (nextStage) {
            this.stage.innerHTML = nextStage.innerHTML;
            this.cloudimage = this.querySelector('.cloudimage-360');
            this.observeCursor();
            if (window.CI360 && typeof window.CI360.init === 'function') {
              window.CI360.init();
            }
          }

          history.replaceState(null, '', `${window.location.pathname}?variant=${id}`);
        })
        .catch((err) => console.error('[product-360-view] variant load failed:', err))
        .finally(() => {
          setTimeout(() => {
            if (this.stage) this.stage.style.height = 'auto';
          }, 1200);
        });
    }

    /* ---------------- Drag pill (watch plugin cursor state) --------------- */

    observeCursor() {
      if (!this.cloudimage) return;
      if (this.cursorObserver) this.cursorObserver.disconnect();

      this.cursorObserver = new MutationObserver(() => {
        const cursor = getComputedStyle(this.cloudimage).cursor;
        if (cursor === 'grab') this.cloudimage.classList.add('loaded');
        else if (cursor === 'wait') this.cloudimage.classList.remove('loaded');
      });

      this.cursorObserver.observe(this.cloudimage, {
        attributes: true,
        attributeFilter: ['style'],
      });
    }

    /* ------------------------- Sticky booking bar ------------------------ */

    initSticky() {
      if (this.dataset.stickyEnabled !== 'true') return;

      this.sticky = this.querySelector('.p360-sticky');
      if (!this.sticky) return;

      this.appearOffset = parseInt(this.dataset.stickyAppear, 10) || 0;
      this.appearSelector = (this.dataset.stickyAppearSelector || '').trim();
      this.appearEl = this.appearSelector ? document.querySelector(this.appearSelector) : null;

      this.hideOffset = parseInt(this.dataset.stickyHideOffset, 10) || 0;
      this.hideSelector = (this.dataset.stickyHideSelector || '').trim();
      this.hideEl = this.hideSelector ? document.querySelector(this.hideSelector) : null;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.sticky.style.transition = 'none';
      }

      this._ticking = false;
      this._onScroll = () => {
        if (this._ticking) return;
        this._ticking = true;
        requestAnimationFrame(() => {
          this.updateSticky();
          this._ticking = false;
        });
      };
      this._onResize = () => {
        this.measure();
        this.updateSticky();
      };

      this.measure();
      this.updateSticky();

      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize);
      window.addEventListener('load', this._onResize, { once: true });
    }

    measure() {
      this.sectionTop = this.getBoundingClientRect().top + window.pageYOffset;
      if (this.appearSelector && !this.appearEl) {
        this.appearEl = document.querySelector(this.appearSelector);
      }
      if (this.hideSelector && !this.hideEl) {
        this.hideEl = document.querySelector(this.hideSelector);
      }
    }

    updateSticky() {
      const y = window.pageYOffset;

      if (this.appearSelector && !this.appearEl) {
        this.appearEl = document.querySelector(this.appearSelector);
      }

      let show = false;

      if (this.appearEl) {
        // Appear when the target element top reaches the viewport threshold
        const top = this.appearEl.getBoundingClientRect().top;
        show = top <= this.appearOffset;
      } else {
        // Fallback: appear after scrolling past section top + offset
        show = y >= this.sectionTop + this.appearOffset;
      }

      if (show) {
        if (this.hideSelector && !this.hideEl) {
          this.hideEl = document.querySelector(this.hideSelector);
        }
        if (this.hideEl) {
          const top = this.hideEl.getBoundingClientRect().top;
          if (top <= window.innerHeight - this.hideOffset) show = false;
        }
      }

      this.sticky.classList.toggle('is-visible', show);
    }

    teardownSticky() {
      if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
      if (this._onResize) window.removeEventListener('resize', this._onResize);
    }
  }

  customElements.define('product-360-view', Product360View);
}
